import * as Yup from 'yup';
import { debounce } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useCallback, useEffect, useMemo, useState } from 'react';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import FormControlLabel from '@mui/material/FormControlLabel';
import {
  Autocomplete,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
} from '@mui/material';

import { RouterLink } from 'src/routes/components';

import { getPaginatedUnits } from 'src/redux/slices/units';
import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import { getPaginatedSuppliers } from 'src/redux/slices/suppliers';
import { getPaginatedPackagingProducts } from 'src/redux/slices/packaging-product';
import {
  createPackagingItem,
  TCreatePackagingItem,
  updatePackagingItem,
} from 'src/redux/slices/packaging-item';

import { useSnackbar } from 'src/components/snackbar';
import FormProvider, { RHFTextField } from 'src/components/hook-form';

import { IUnitListItem } from 'src/types/unit';
import { ISupplierListItem } from 'src/types/supplier';
import { IPackagingItem } from 'src/types/packaging-item';
import { IPackagingProductListItem } from 'src/types/packaging-product';

import { paths } from '../../routes/paths';

type Props = {
  currentPackagingItem?: IPackagingItem;
};

export default function PackagingItemNewEditForm({ currentPackagingItem }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const dispatch = useAppDispatcher();

  const { suppliers, status } = useAppSelector((state) => state.suppliers);
  const { units } = useAppSelector((state) => state.units);
  const { products } = useAppSelector((state) => state.packagingProduct);

  const [searchQuery, setSearchQuery] = useState('');

  const [selectedSuppliers, setSelectedSuppliers] = useState<ISupplierListItem[]>(
    currentPackagingItem?.suppliers ?? []
  );
  const [localSuppliers, setLocalSuppliers] = useState<ISupplierListItem[]>([]);

  const NewPackagingItemSchema = Yup.object().shape({
    itemName: Yup.string().required('Item name is required'),
    unit: Yup.string().required('Unit is required'),
    initialQuantity: Yup.number().optional().nullable().default(0),
    reorderThreshold: Yup.number().optional().nullable().default(0),
    alertEnabled: Yup.boolean().optional().default(true),
    product: Yup.string().nullable(),
  });

  const defaultValues = useMemo(
    () => ({
      itemName: currentPackagingItem?.itemName || '',
      unit: currentPackagingItem?.unit._id || '',
      initialQuantity: 0,
      reorderThreshold: currentPackagingItem?.reorderThreshold ?? 0,
      alertEnabled: currentPackagingItem?.alertEnabled ?? true,
      product: currentPackagingItem?.product?._id ?? null,
    }),
    [currentPackagingItem]
  );

  const methods = useForm({
    resolver: yupResolver(NewPackagingItemSchema),
    defaultValues,
    mode: 'onChange',
  });

  const {
    reset,
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (currentPackagingItem) {
        await dispatch(
          updatePackagingItem({
            itemId: currentPackagingItem._id,
            updatingItem: {
              itemName: data.itemName.trimStart().replace(/^\w/, (c) => c.toUpperCase()),
              unit: data.unit,
              reorderThreshold: data.reorderThreshold,
              alertEnabled: data.alertEnabled,
              suppliers: selectedSuppliers.map((supplier) => supplier._id),
              product: data.product ?? null,
            },
          })
        ).unwrap();

        enqueueSnackbar('Packaging item edit success');
      } else {
        const newItem: TCreatePackagingItem = {
          ...data,
          product: data.product || undefined,
          itemName: data.itemName.trimStart().replace(/^\w/, (c) => c.toUpperCase()),
          suppliers: selectedSuppliers.map((supplier) => supplier._id),
        };

        await dispatch(createPackagingItem({ newItem })).unwrap();

        enqueueSnackbar('Packaging item creation success');
      }

      navigate(paths.dashboard.packagingItem.root);
      reset();
    } catch (e) {
      if (typeof e === 'string') {
        enqueueSnackbar(e, {
          variant: 'error',
        });
      } else {
        enqueueSnackbar(e?.message, {
          variant: 'error',
        });
      }
    }
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchPaginatedSuppliers = useCallback(
    debounce(async (query) => {
      dispatch(
        getPaginatedSuppliers({
          page: null,
          limit: null,
          generalSearch: query,
          sort: 'asc',
          sortBy: 'supplierName',
        })
      );
    }, 500),
    [dispatch]
  );

  useEffect(() => {
    fetchPaginatedSuppliers(searchQuery);

    return fetchPaginatedSuppliers.cancel;
  }, [searchQuery, fetchPaginatedSuppliers]);

  useEffect(() => {
    setLocalSuppliers(suppliers.suppliers);
  }, [suppliers]);

  useEffect(() => {
    dispatch(getPaginatedUnits({}));
    dispatch(
      getPaginatedPackagingProducts({
        page: null,
        limit: null,
        generalSearch: null,
        sortBy: 'productName',
        sort: 'asc',
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Grid container spacing={3}>
        <Grid xs={12}>
          <Card sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid
                mb={1}
                display="flex"
                mt={2}
                alignItems="center"
                justifyContent="space-between"
                width={1}
              >
                <Typography variant="subtitle2">Basic Information</Typography>
              </Grid>

              <Grid xs={12} md={6}>
                <RHFTextField required name="itemName" label="Item name" />
              </Grid>

              <Grid xs={12} md={6}>
                <Controller
                  name="unit"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      required
                      sx={{
                        flexShrink: 0,
                        width: { xs: 1 },
                      }}
                    >
                      <InputLabel>Unit</InputLabel>

                      <Select
                        {...field}
                        multiple={false}
                        input={<OutlinedInput label="Unit" />}
                        MenuProps={{
                          PaperProps: {
                            sx: { maxHeight: 240 },
                          },
                        }}
                      >
                        {units.units.map((option: IUnitListItem) => (
                          <MenuItem key={option._id} value={option._id}>
                            {option.abbreviation}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid xs={12} md={6}>
                <Controller
                  name="product"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={products.items as IPackagingProductListItem[]}
                      value={
                        (products.items as IPackagingProductListItem[]).find(
                          (option) => option._id === field.value
                        ) ??
                        (currentPackagingItem?.product?._id === field.value
                          ? (currentPackagingItem?.product as IPackagingProductListItem)
                          : null)
                      }
                      onChange={(_, option) => field.onChange(option?._id ?? null)}
                      isOptionEqualToValue={(option, value) => option._id === value._id}
                      getOptionLabel={(option) => option.productName}
                      renderOption={(props, option) => (
                        <li {...props} key={option._id}>
                          {option.productName}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Linked Product (Optional)"
                          helperText="When set, dispatches of this item always use this product"
                        />
                      )}
                    />
                  )}
                />
              </Grid>

              {!currentPackagingItem && (
                <Grid xs={12} md={6}>
                  <RHFTextField
                    name="initialQuantity"
                    label="Initial Quantity (already in stock)"
                    type="number"
                  />
                </Grid>
              )}

              <Divider
                sx={{
                  width: 1,
                  my: 2.5,
                  borderStyle: 'dashed',
                }}
              />

              <Grid mb={1} xs={12} width="full">
                <Typography mt={2} variant="subtitle2">
                  Additional Information
                </Typography>
              </Grid>

              <Grid xs={12} md={6}>
                <RHFTextField name="reorderThreshold" label="Re-order Threshold" type="number" />
              </Grid>

              <Grid xs={12} md={6}>
                <FormControlLabel
                  labelPlacement="start"
                  control={
                    <Controller
                      name="alertEnabled"
                      control={control}
                      render={({ field }) => <Switch {...field} checked={field.value} />}
                    />
                  }
                  label={
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                        Stock Alerts Enabled
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Receive stock alerts when this item is low or running low
                      </Typography>
                    </>
                  }
                  sx={{ mx: 0, mb: 3, width: 1, justifyContent: 'space-between' }}
                />
              </Grid>

              <Divider
                sx={{
                  width: 1,
                  my: 2.5,
                  borderStyle: 'dashed',
                }}
              />

              <Grid mb={1} xs={12} width="full">
                <Typography mt={2} variant="subtitle2">
                  Supplier Information
                </Typography>
              </Grid>

              <Grid xs={12}>
                <Autocomplete
                  multiple
                  getOptionLabel={(option) =>
                    typeof option === 'string' ? option : option.supplierName
                  }
                  filterOptions={(x) => x}
                  options={localSuppliers}
                  autoComplete
                  isOptionEqualToValue={(option, value) => option._id === value._id}
                  includeInputInList
                  filterSelectedOptions
                  value={selectedSuppliers}
                  loading={status === 'loading'}
                  loadingText="Loading..."
                  noOptionsText="No Suppliers found..."
                  onChange={(event: any, newValue: ISupplierListItem[] | null) => {
                    const uniqueSuppliers = Array.from(
                      new Set([...(newValue || []), ...localSuppliers])
                    );
                    setLocalSuppliers(uniqueSuppliers);

                    setSelectedSuppliers(newValue ?? []);
                  }}
                  onInputChange={(event, newInputValue) => {
                    setSearchQuery(newInputValue);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Search for suppliers..." fullWidth />
                  )}
                  renderOption={(props, option) => (
                    <li key={option._id} {...props}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {option.supplierName}
                      </Typography>
                    </li>
                  )}
                />
              </Grid>
            </Grid>

            <Stack direction="row" justifyContent="flex-end" gap={2} mt={4}>
              <Link component={RouterLink} href={paths.dashboard.packagingItem.root}>
                <Button variant="outlined" color="inherit">
                  Cancel
                </Button>
              </Link>

              <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
                {!currentPackagingItem ? 'Create Packaging Item' : 'Save Changes'}
              </LoadingButton>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
}
